import { useForm } from "react-hook-form";
import type { MutateOptions } from "react-query";
import type { GenericLink } from "types";
import type { Collective, CollectiveFormFields } from "../types";
import { useCreateCollective } from "./useCreateCollective";
import { useUpdateCollective } from "./useUpdateCollective";

// DRAFT PROFILES - If the user is a collective admin (not a HUG admin)
// then show a PUBLISH button instead of a UPDATE button. We use a hidden
// field to indicate forcePublish to useCollectiveForm.

type CollectiveFieldsForForm = CollectiveFormFields & {
  forcePublish?: boolean;
};

type UseCollectiveFormParams = {
  collective?: CollectiveFieldsForForm;
} & MutateOptions<Collective, unknown, CollectiveFormFields>;

/**
 * Convert array of links into specified separate inputs in order for Collective form
 */
const transformLinks = (links: GenericLink[] = []): GenericLink[] =>
  ["website", "instagram", "twitter"].map((type) => ({
    type,
    url: links.find((l) => l.type === type)?.url ?? "",
  }));

const useCollectiveForm = ({
  collective,
  ...options
}: UseCollectiveFormParams) => {
  // Transform collective into default values, if necessary
  const { handleSubmit, ...form } = useForm<CollectiveFieldsForForm>({
    defaultValues: collective
      ? {
          ...collective,
          links: transformLinks(collective.links),
        }
      : {
          affiliatedArtists: [],
        },
  });

  const createMutation = useCreateCollective();
  const updateMutation = useUpdateCollective();

  const isEdit = !!collective;
  const mutation = isEdit ? updateMutation : createMutation;

  const onSubmit = handleSubmit(
    ({ banner, links, status, forcePublish, ...formData }) => {
      // Transform required field values into request data, if necessary
      const transformed = {
        ...formData,
        // Banner images are optional, so set undefined if URL is an empty string
        banner: banner?.url === "" ? undefined : banner,
        // Links are all optional, so filter out empty strings
        links: links.filter((l) => l.url),
        status: forcePublish ? "PUBLISHED" : status,
      };

      mutation.mutate(
        isEdit ? { ...collective, ...transformed } : transformed,
        options,
      );
    },
  );

  return {
    ...form,
    isEdit,
    mutation,
    onSubmit,
  };
};

export { useCollectiveForm };
